import React, { useState } from "react";

// ---------------------------------------------------------------------------
// Spielende-Modal: Endstand, "بازیکن برتر مسابقه", Level-Glueckwunsch und die
// komplette Spielhistorie (jede Runde, jeder Stich mit allen 4 Karten).
//
// Bewusst OHNE Auto-Close: das Modal bleibt offen, bis der Spieler es selbst
// schliesst. Es haengt kein Socket-Event am Schliessen - jeder Spieler
// entscheidet weiterhin individuell ueber "بازی جدید"/"خروج از میز" im
// Post-Game-Screen darunter, ein offenes Modal blockiert also niemanden.
//
// Daten kommen ausschliesslich aus dem server-seitigen "gameHistory"-Event
// (siehe server/matchSummary.js -> buildGameHistoryPayload).
// ---------------------------------------------------------------------------

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
export const faNum = (n) => String(n ?? "").replace(/\d/g, (d) => FA_DIGITS[d]);
const signedText = (n) => (n > 0 ? `+${faNum(n)}` : n < 0 ? `−${faNum(-n)}` : faNum(0));
// Vorzeichen-Zahlen in RTL isolieren - sonst rendert der Browser "+۱۵" als "۱۵+".
const Ltr = ({ children }) => <bdi dir="ltr">{children}</bdi>;
const Signed = ({ n }) => <Ltr>{signedText(n)}</Ltr>;

export const teamFa = (team) =>
  team === "Fire" ? "آتش" : team === "Storm" ? "طوفان" : team || "؟";
const teamColor = (team) =>
  team === "Fire" ? "var(--fire)" : team === "Storm" ? "var(--storm)" : "var(--muted)";

const TIE_TEXT = {
  tricks: "تساوی امتیاز — برتری با تعداد دست‌های برده",
  bidderPoints: "تساوی امتیاز و دست — برتری با امتیاز به‌عنوان حاکم",
  bidsMade: "تساوی کامل — برتری با تعداد هدف‌های رسیده به‌عنوان حاکم",
  userId: "تساوی کامل در همهٔ معیارها",
};

function TeamTag({ team }) {
  return (
    <span className="sh-ge-team" style={{ color: teamColor(team), borderColor: teamColor(team) }}>
      {teamFa(team)}
    </span>
  );
}

function PlayerOfMatch({ pom }) {
  if (!pom) return null;
  return (
    <section className="sh-ge-pom" aria-label="بازیکن برتر مسابقه">
      <div className="sh-ge-pomhd">🏅 بازیکن برتر مسابقه</div>
      <div className="sh-ge-pomname">
        {pom.name || "—"} <TeamTag team={pom.team} />
      </div>
      <div className="sh-ge-pomtotal">
        {faNum(pom.total)} امتیاز · {faNum(pom.tricksWon)} دستِ برده
      </div>
      <div className="sh-ge-roles">
        <div className="sh-ge-role">
          <span>به‌عنوان حاکم</span>
          <b>{faNum(pom.byRole?.bidder ?? 0)}</b>
        </div>
        <div className="sh-ge-role">
          <span>یار حاکم</span>
          <b>{faNum(pom.byRole?.partner ?? 0)}</b>
        </div>
        <div className="sh-ge-role">
          <span>در برابر حاکم</span>
          <b>{faNum(pom.byRole?.opponent ?? 0)}</b>
        </div>
      </div>
      {pom.decidedBy && pom.decidedBy !== "points" && (
        <div className="sh-ge-note">{TIE_TEXT[pom.decidedBy]}</div>
      )}
    </section>
  );
}

function PlayersTable({ players, meId }) {
  if (!players?.length) return null;
  return (
    <div className="sh-ge-scrollx">
      <table className="sh-ge-table">
        <thead>
          <tr>
            <th>بازیکن</th>
            <th>امتیاز</th>
            <th>حاکم</th>
            <th>یار</th>
            <th>حریف</th>
            <th>دست</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.userId} className={p.userId === meId ? "is-me" : undefined}>
              <td>
                <span style={{ color: teamColor(p.team), fontWeight: 800 }}>{p.name || "—"}</span>
              </td>
              <td>
                <b>{faNum(p.total)}</b>
              </td>
              <td>{faNum(p.byRole?.bidder ?? 0)}</td>
              <td>{faNum(p.byRole?.partner ?? 0)}</td>
              <td>{faNum(p.byRole?.opponent ?? 0)}</td>
              <td>{faNum(p.tricksWon)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundResult({ r }) {
  return r.bidSuccess ? (
    <span className="sh-ge-res ok">✓ هدف رسید</span>
  ) : (
    <span className="sh-ge-res bad">✗ هدف نرسید</span>
  );
}

function RoundHead({ r }) {
  return (
    <div className="sh-ge-rhead">
      <b>دور {faNum(r.round)}</b>
      <span>
        حاکم: <span style={{ color: teamColor(r.bidderTeam) }}>{r.bidderName || "—"}</span>
      </span>
      <span>هدف {faNum(r.bid)}</span>
      <span>حکم {r.trumpf || (r.variant === "FLIP" ? "نرس" : "—")}</span>
      <RoundResult r={r} />
    </div>
  );
}

function RoundScores({ r }) {
  return (
    <div className="sh-ge-rscores">
      <span>
        <TeamTag team="Fire" /> {faNum(r.roundPoints.Fire)}
        {r.deltaApplied && (
          <>
            {" "}(<Signed n={r.deltaApplied.Fire ?? 0} />)
          </>
        )}
      </span>
      <span>
        <TeamTag team="Storm" /> {faNum(r.roundPoints.Storm)}
        {r.deltaApplied && (
          <>
            {" "}(<Signed n={r.deltaApplied.Storm ?? 0} />)
          </>
        )}
      </span>
      <span className="sh-ge-after">
        مجموع: آتش {faNum(r.teamScoresAfter.Fire)} · طوفان {faNum(r.teamScoresAfter.Storm)}
      </span>
      {r.ruleApplied === "doublePositive" && <span className="sh-ge-res ok">دوبل مثبت</span>}
      {r.ruleApplied === "doubleNegative" && <span className="sh-ge-res bad">دوبل منفی</span>}
    </div>
  );
}

function CardStrip({ title, cards, renderCard }) {
  return (
    <div className="sh-ge-strip">
      <div className="sh-ge-striphd">{title}</div>
      <div className="sh-ge-cards">
        {cards?.length ? (
          cards.map((c, i) => <div key={`${c}-${i}`}>{renderCard(c)}</div>)
        ) : (
          <span className="sh-ge-muted">بدون کارت</span>
        )}
      </div>
    </div>
  );
}

function TrickDetail({ t, renderCard }) {
  return (
    <div className="sh-ge-trick" style={{ borderInlineStartColor: teamColor(t.winnerTeam) }}>
      <div className="sh-ge-trickhd">
        <b>دست {faNum(t.no)}</b>
        {t.leadSuit && <span className="sh-ge-muted">خال {t.leadSuit}</span>}
        <span className="sh-ge-grow">
          برنده: <span style={{ color: teamColor(t.winnerTeam), fontWeight: 800 }}>{t.winnerName || "—"}</span>
        </span>
        <span className="sh-ge-pts">
          <Ltr>+{faNum(t.points)}</Ltr>
        </span>
      </div>
      <div className="sh-ge-plays">
        {(t.plays || []).map((p, i) => (
          <div
            key={`${p.userId}-${i}`}
            className={"sh-ge-play" + (p.userId === t.winnerId ? " is-win" : "")}
          >
            {renderCard(p.card)}
            <span className="sh-ge-pname" style={{ color: teamColor(p.team) }}>
              {p.name || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoundsDetail({ rounds, renderCard, truncated, roundCount }) {
  const [open, setOpen] = useState({});
  if (!rounds?.length) return <div className="sh-ge-muted">دوری ثبت نشده است</div>;
  return (
    <div className="sh-ge-rounds">
      {truncated && (
        <div className="sh-ge-note">
          فقط {faNum(rounds.length)} دور آخر از {faNum(roundCount)} دور نمایش داده می‌شود
        </div>
      )}
      {rounds.map((r) => {
        const isOpen = !!open[r.round];
        return (
          <div key={r.round} className="sh-ge-round">
            <button
              type="button"
              className="sh-ge-rbtn"
              aria-expanded={isOpen}
              onClick={() => setOpen((o) => ({ ...o, [r.round]: !o[r.round] }))}
            >
              <RoundHead r={r} />
              <span className="sh-ge-chev">{isOpen ? "▲" : "▼"}</span>
            </button>
            <RoundScores r={r} />
            {isOpen && (
              <div className="sh-ge-rbody">
                <CardStrip title="📤 کارت‌های زمین" cards={r.bottomCards} renderCard={renderCard} />
                <CardStrip
                  title={
                    <>
                      🗑️ خوابانده (حاکم){" "}
                      {r.discardPoints ? <Ltr>+{faNum(r.discardPoints)}</Ltr> : null}
                    </>
                  }
                  cards={r.discarded}
                  renderCard={renderCard}
                />
                {(r.tricks || []).map((t) => (
                  <TrickDetail key={t.no} t={t} renderCard={renderCard} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function GameEndModal({ history, meId, onClose, renderCard }) {
  const [tab, setTab] = useState("summary");
  if (!history) return null;
  const { winner, teamScores, playerOfMatch, players, rounds, levelUp } = history;

  return (
    <div className="sh-sheet sh-sheet--gameend" role="dialog" aria-modal="true" aria-label="پایان بازی">
      <div className="sh-sheetbox sh-sheetbox--wide" dir="rtl">
        <div className="sh-sheethd">
          <h2>{winner === "Fire" ? "🔥 تیم آتش برنده شد" : winner === "Storm" ? "🌩️ تیم طوفان برنده شد" : "پایان بازی"}</h2>
          <button type="button" className="sh-xbtn" onClick={onClose} aria-label="بستن">
            ×
          </button>
        </div>

        {teamScores && (
          <div className="sh-ge-final">
            <span style={{ color: "var(--fire)" }}>آتش {faNum(teamScores.Fire ?? 0)}</span>
            <span className="sh-ge-muted">—</span>
            <span style={{ color: "var(--storm)" }}>طوفان {faNum(teamScores.Storm ?? 0)}</span>
          </div>
        )}

        {levelUp && (
          <div className="sh-ge-levelup" role="status">
            🎉 تبریک! به سطح {faNum(levelUp.to)}
            {levelUp.title ? ` («${levelUp.title}»)` : ""} رسیدی
            <span className="sh-ge-muted"> · از سطح {faNum(levelUp.from)}</span>
          </div>
        )}

        <PlayerOfMatch pom={playerOfMatch} />

        <div className="sh-ge-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "summary"}
            className={"sh-btn" + (tab === "summary" ? " sh-btn--gold" : "")}
            onClick={() => setTab("summary")}
          >
            خلاصهٔ بازی
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "rounds"}
            className={"sh-btn" + (tab === "rounds" ? " sh-btn--gold" : "")}
            onClick={() => setTab("rounds")}
          >
            همهٔ دورها و دست‌ها
          </button>
        </div>

        {tab === "summary" ? (
          <>
            <div className="sh-ge-sub">امتیاز فردی بازیکنان</div>
            <PlayersTable players={players} meId={meId} />
            <div className="sh-ge-hint">
              امتیاز فردی = مجموع امتیاز دست‌هایی که خود بازیکن برده است، بر اساس نقش او در همان دور
              (امتیاز کارت‌های خوابانده به حساب حاکم).
            </div>
            <div className="sh-ge-sub">دورها</div>
            <div className="sh-ge-rounds">
              {(rounds || []).map((r) => (
                <div key={r.round} className="sh-ge-round">
                  <RoundHead r={r} />
                  <RoundScores r={r} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <RoundsDetail
            rounds={rounds}
            renderCard={renderCard}
            truncated={history.truncated}
            roundCount={history.roundCount}
          />
        )}

        <div className="sh-row" style={{ marginTop: 16 }}>
          <button type="button" className="sh-btn" style={{ minWidth: 140 }} onClick={onClose}>
            بستن
          </button>
        </div>
      </div>
    </div>
  );
}
